/**
 * Copyright (c) 2021-2024 Kognitos, Inc. All rights reserved.
 */
import { useSearchParams } from 'react-router-dom';
import { useCallback, useEffect, useMemo } from 'react';

type PossibleValues = string | number | Date | undefined | null;

type FilterValue = PossibleValues | PossibleValues[];

type FilterConfig<T> = {
  [key in keyof T]: FilterValue; // Default values directly associated with each key
};

/**
 * Deserialize function that can handle both single values and arrays.
 */
const deserializeValue = (value: string | null, isArray = false) => {
  if (!value) return isArray ? [] : undefined;
  if (isArray || value.includes(',')) {
    return value.split(',').map(decodeURIComponent);
  }
  return decodeURIComponent(value);
};

/**
 * Serialize function that can handle both single values and arrays.
 */
const serializeValue = (value: FilterValue) => {
  if (Array.isArray(value)) {
    return value.map((v) => encodeURIComponent(v?.toString() || '')).join(',');
  }
  return encodeURIComponent(value?.toString() || '');
};

/**
 * Hook for managing URL filters with support for array parameters.
 * @param config - The configuration object for the filters. Enables default values for each filter
 * if there are no url search params and the page is opened the very first time
 * @returns A tuple containing the filters object and a function to update the filters.
 *
 * * @example
 * ```typescript
 * const filterConfig: FilterType = {
 *  status: ['pending'],
 *  dateRange: [],
 * };
 *
 * const [filters, setFilters] = useURLFilters<FilterType>(filterConfig);
 * ```
 */
export function useURLFilters<T extends Record<keyof T, FilterValue>>(
  defaultFilterConfig: FilterConfig<T>
) {
  const [searchParams, setSearchParams] = useSearchParams();

  /**
   * Initialize the filters based on the URL search parameters.
   */
  const filters: T = useMemo(() => {
    const initialFilters: FilterConfig<T> = {} as FilterConfig<T>;

    const hasSearchParams = Array.from(searchParams.keys()).length > 0;

    for (const key in defaultFilterConfig) {
      if (Object.prototype.hasOwnProperty.call(defaultFilterConfig, key)) {
        const isValueArray = Array.isArray(defaultFilterConfig[key]);
        if (hasSearchParams) {
          const value = searchParams.get(key);
          initialFilters[key] = deserializeValue(value, isValueArray);
        } else {
          initialFilters[key] = isValueArray ? [] : undefined;
        }
      }
    }
    return initialFilters as T;
  }, [searchParams, defaultFilterConfig]);

  /**
   * Update the filters and update the URL search parameters accordingly.
   *
   * @param newFilters - The new filters to be set.
   * @param forceClear - A boolean indicating whether to force clear the filters.
   */
  const setFilters = useCallback(
    (newFilters: Partial<FilterConfig<T>>, forceClear: boolean = false) => {
      const newSearchParams = new URLSearchParams();

      const hasNewFilters = Object.values(newFilters).reduce((acc, value) => {
        return (
          acc || (Array.isArray(value) ? value.length : value !== undefined)
        );
      }, false);

      if (forceClear || !hasNewFilters) {
        newSearchParams.set('clear', 'true');
      } else {
        for (const key in newFilters) {
          if (Object.prototype.hasOwnProperty.call(newFilters, key)) {
            const value = newFilters[key];
            if (
              value === undefined ||
              (Array.isArray(value) && !value.length)
            ) {
              newSearchParams.delete(key);
            } else {
              newSearchParams.set(key, serializeValue(value));
            }
          }
        }

        newSearchParams.delete('clear');
      }

      setSearchParams(newSearchParams, { replace: true });
    },
    [setSearchParams]
  );

  const initURLWithDefaults = useCallback(() => {
    // Initialize the filters based on the URL search parameters.
    const hasSearchParams = Array.from(searchParams.keys()).length > 0;
    if (!hasSearchParams) {
      setFilters(defaultFilterConfig);
    }
  }, [defaultFilterConfig, searchParams, setFilters]);

  useEffect(() => {
    initURLWithDefaults();
  }, [initURLWithDefaults]);

  return [filters, setFilters] as const;
}
